import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Printer, Users, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const csvEscape = (s) => `"${String(s || "").replace(/"/g, '""')}"`;

export default function ContactsExportModal({ onClose }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/contacts/export")
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Não consegui carregar os contatos"); onClose(); });
  }, []); // eslint-disable-line

  const contacts = (data?.contacts || []).filter((c) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return c.name.toLowerCase().includes(f) || c.phone.includes(filter);
  });

  const downloadCsv = () => {
    const rows = [["Nome", "Telefone", "Origem"], ...contacts.map((c) => [c.name, c.phone, c.source])];
    const csv = "\uFEFF" + rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `contatos-intercar-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${contacts.length} contatos exportados`);
  };

  const printList = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Libere pop-ups pra imprimir"); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>Contatos Intercar</title>
      <style>
        body{font-family:Helvetica,Arial,sans-serif;margin:28px;color:#111}
        h1{font-size:16px;text-transform:uppercase;border-bottom:3px solid #C9A227;padding-bottom:8px}
        p.meta{font-size:11px;color:#666;margin:4px 0 14px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th{text-align:left;background:#101010;color:#C9A227;padding:5px 8px;text-transform:uppercase;font-size:9px;letter-spacing:1px}
        td{padding:4px 8px;border-bottom:1px solid #ddd}
        tr:nth-child(even) td{background:#f6f4ee}
      </style></head><body>
      <h1>Intercar — Lista de Contatos</h1>
      <p class="meta">${contacts.length} contatos · gerado em ${new Date().toLocaleDateString("pt-BR")}</p>
      <table><thead><tr><th style="width:36px">#</th><th>Nome</th><th>Telefone</th><th>Origem</th></tr></thead><tbody>
      ${contacts.map((c, i) => `<tr><td>${i + 1}</td><td>${c.name}</td><td>${c.phone}</td><td>${c.source}</td></tr>`).join("")}
      </tbody></table></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="contacts-export-modal" className="bg-surface border border-border w-full max-w-xl p-5 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <p className="label-eyebrow text-primary flex items-center gap-2"><Users size={13} /> Exportar Contatos (Nome + Telefone)</p>
          <button type="button" onClick={onClose} data-testid="contacts-export-close" className="text-text-secondary hover:text-white transition-colors"><X size={16} /></button>
        </div>
        <p className="text-xs text-text-secondary mb-3">Todos os leads + compradores, sem telefone repetido — pronto pra campanha de WhatsApp/SMS por fora.</p>

        {!data ? (
          <p className="text-sm text-text-secondary py-10 text-center">Carregando contatos...</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <p className="font-display font-black text-2xl text-[#C9A227]" data-testid="contacts-total">{contacts.length}</p>
              <p className="text-xs text-text-secondary">contatos{filter ? ` (de ${data.total})` : ""}</p>
              <div className="border border-border flex items-center px-3 h-9 flex-1 min-w-[160px] ml-auto">
                <Search size={13} className="text-text-secondary mr-2" />
                <input data-testid="contacts-filter" type="text" placeholder="Filtrar..." value={filter} onChange={(e) => setFilter(e.target.value)}
                  className="bg-transparent w-full focus:outline-none text-xs" />
              </div>
            </div>

            <div className="border border-border overflow-y-auto flex-1 min-h-[200px] max-h-[46vh]">
              <table className="w-full text-xs">
                <thead className="bg-background sticky top-0">
                  <tr>
                    <th className="text-left p-2 label-eyebrow">Nome</th>
                    <th className="text-left p-2 label-eyebrow">Telefone</th>
                    <th className="text-left p-2 label-eyebrow">Origem</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="p-2 font-display font-bold">{c.name}</td>
                      <td className="p-2 font-mono">{c.phone}</td>
                      <td className="p-2">
                        <span className={`text-[9px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 border ${c.source === "comprador" ? "border-success text-success" : "border-border text-text-secondary"}`}>
                          {c.source}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 mt-4">
              <button type="button" data-testid="contacts-download-csv" onClick={downloadCsv}
                className="flex-1 bg-primary text-white hover:opacity-90 h-11 inline-flex items-center justify-center gap-2 font-display font-bold uppercase text-xs tracking-widest transition-opacity">
                <Download size={14} /> Baixar CSV (Excel)
              </button>
              <button type="button" data-testid="contacts-print" onClick={printList}
                className="flex-1 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 h-11 inline-flex items-center justify-center gap-2 font-display font-bold uppercase text-xs tracking-widest transition-colors">
                <Printer size={14} /> Imprimir Lista
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
